import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import useClassService from '../../api/services/ClassService';

const GenerateIndexNumberDialog = ({ open, onClose, onSuccess, classId, generateStudentsNumberByClassId }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classInfo, setClassInfo] = useState(null);
  const [loadingClassInfo, setLoadingClassInfo] = useState(false);
  const { getClassById } = useClassService();
  const fetchedClassIdRef = useRef(null);

  useEffect(() => {
    const fetchClassInfo = async () => {
      // Only fetch if dialog is open, classId exists, and we haven't already fetched for this classId
      if (open && classId && fetchedClassIdRef.current !== classId) {
        setLoadingClassInfo(true);
        fetchedClassIdRef.current = classId;
        try {
          const classData = await getClassById(classId);
          setClassInfo(classData);
        } catch (error) {
          console.error('Error fetching class info:', error);
          setClassInfo(null);
          fetchedClassIdRef.current = null;
        } finally {
          setLoadingClassInfo(false);
        }
      } else if (!open) {
        // Reset when dialog closes
        setClassInfo(null);
        fetchedClassIdRef.current = null;
      }
    };

    fetchClassInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classId]);

  // Determine if it's A-LEVEL
  const isALevel = classInfo?.classType && (
    classInfo.classType.toUpperCase().includes('A-LEVEL') ||
    classInfo.classType.toUpperCase().includes('A LEVEL') ||
    classInfo.classType.toUpperCase().includes('ALEVEL')
  );

  // Dynamic validation schema based on class type
  const validationSchema = Yup.object().shape({
    classFinalYear: isALevel
      ? Yup.string()
          .matches(/^\d{4}$|^$/, 'Year must be a 4-digit number or empty')
      : Yup.string()
          .required('Class Final Year is required')
          .matches(/^\d{4}$/, 'Year must be a 4-digit number'),
  });

  const handleSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      // Pass the value entered (or empty string if not provided)
      // Backend will use the provided year if available, otherwise use current year for A-LEVEL
      const classFinalYear = values.classFinalYear || null;
      await generateStudentsNumberByClassId(classId, classFinalYear);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error generating index numbers:', error);
      // Error toast is already handled in the service
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>GENERATE INDEX NUMBERS</DialogTitle>
      {loadingClassInfo ? (
        <DialogContent>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        </DialogContent>
      ) : (
        <Formik
          initialValues={{
            classFinalYear: isALevel ? '' : currentYear.toString(),
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ values, errors, touched, handleChange, handleBlur }) => (
            <Form>
              <DialogContent>
                <Box sx={{ mt: 1 }}>
                  {classInfo && (
                    <Typography variant="body2" color="primary" sx={{ mb: 2, fontWeight: 'bold' }}>
                      Class Type: {classInfo.classType || 'N/A'}
                    </Typography>
                  )}
                  <TextField
                    fullWidth
                    label="Class Final Year"
                    name="classFinalYear"
                    value={values.classFinalYear}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.classFinalYear && Boolean(errors.classFinalYear)}
                    helperText={
                      touched.classFinalYear && errors.classFinalYear
                        ? errors.classFinalYear
                        : isALevel
                        ? ''
                        : 'Required: Enter the final year for the class (e.g., 2024). O-Level numbers start from 0001.'
                    }
                    variant="filled"
                    type="text"
                    inputProps={{
                      maxLength: 4,
                      pattern: '[0-9]*',
                    }}
                    placeholder={isALevel ? 'Leave empty for current year' : 'e.g., 2024'}
                  />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    {isALevel
                      ? 'A-Level: Index numbers will start from 0501. Use the year you enter above, or leave empty to use current year.'
                      : 'O-Level: Index numbers will start from 0001 and use the year you enter above.'}
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ p: 2 }}>
                <Button onClick={onClose} disabled={isSubmitting}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting || loadingClassInfo}
                  startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
                >
                  {isSubmitting ? 'Generating...' : 'Generate'}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default GenerateIndexNumberDialog;

